import { Box, Flex, Group, Stack, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import CashflowSection from '../components/CashflowSection';
import CashflowTable from '../components/CashflowTable';
import { LeftOutNote } from '../components/LeftOutNote';
import { PeriodToggle } from '../components/PeriodToggle';
import { SectionTotal } from '../components/cashflowCells';
import {
  INCOME_TOTAL_REASON_LABELS,
  LeftOutNames,
  ReasonLabels,
  afterTaxLeftOutReason,
  combineLeftOutNames,
  expenditureLeftOutReason,
  expenditureName,
  rowLeftOutNames,
  incomeLeftOutReason,
  incomeName,
  lifeEventLeftOutReason,
  lifeEventName,
} from '../leftOut';
import { NO_FIGURE } from '../notRecorded';
import { ExpenditureRow, IncomeRow, LifeEventRow } from '../rows';
import { Period, totalForPeriod } from '../utils';
import { expenditureRowColumns, incomeRowColumns, lifeEventRowColumns } from './redesignColumns';

type RowActions<T> = {
  onAdd?: () => void;
  /** Rows open their item for editing; there are no per-row action buttons. */
  onRowClick?: (row: T) => void;
};

type CashflowsLayoutProps = {
  period: Period;
  onPeriodChange: (period: Period) => void;
  incomeRows: IncomeRow[];
  expenditureRows: ExpenditureRow[];
  lifeEventRows: LifeEventRow[];
  incomeActions?: RowActions<IncomeRow>;
  expenditureActions?: RowActions<ExpenditureRow>;
  lifeEventActions?: RowActions<LifeEventRow>;
  /** Names who each item left out of a total applies to, for a view spanning several members. */
  nameMembers?: boolean;
  banner?: ReactNode;
  modelsSection?: ReactNode;
};

/**
 * The Cashflows tab as both the family and the individual screen render it. One period
 * toggle governs every section, so the sections' figures are always read on the same basis and
 * the net position between them is a sum the adviser can check by eye.
 */
export function CashflowsLayout({
  period,
  onPeriodChange,
  incomeRows,
  expenditureRows,
  lifeEventRows,
  incomeActions,
  expenditureActions,
  lifeEventActions,
  nameMembers = false,
  banner,
  modelsSection,
}: CashflowsLayoutProps) {
  const currency = useCurrency();
  const otherPeriod: Period = period === 'monthly' ? 'annually' : 'monthly';

  // A row shows the whole item, but a shared item adds only the counted part of it to the totals —
  // one client's on their tab, the clients' together on the family's, so the family's totals are
  // the members' added up — and is left out of them only where that part is missing. Being left
  // out is judged on the figures the totals add, so the notes and the totals always agree.
  const countedIncomes = incomeRows.map(row => (row.counted ? { ...row, ...row.counted } : row));
  const countedExpenditures = expenditureRows.map(row =>
    row.counted ? { ...row, ...row.counted } : row,
  );
  const countedLifeEvents = lifeEventRows.map(row =>
    row.counted ? { ...row, ...row.counted } : row,
  );
  const totals = {
    incomePreTax: totalForPeriod(countedIncomes, row => row.amountPreTax, period),
    incomePostTax: totalForPeriod(countedIncomes, row => row.amountPostTax, period),
    expenditure: totalForPeriod(countedExpenditures, row => row.amount, period),
    lifeEvents: countedLifeEvents.reduce((total, row) => total + (row.cost ?? 0), 0),
  };

  // Net position is struck after tax: pre-tax income never reaches the household's pocket, so
  // setting spending against it would overstate what is left over.
  const netPosition = totals.incomePostTax - totals.expenditure;
  // With neither side recorded there is no position to strike. "-£0" reads as a household that
  // breaks exactly even rather than one whose figures have never been entered.
  const hasNetPosition = incomeRows.length > 0 || expenditureRows.length > 0;

  const otherFrequency = otherPeriod === 'monthly' ? 'Monthly' : 'Annually';
  const incomeOtherPreTax = totalForPeriod(countedIncomes, row => row.amountPreTax, otherPeriod);
  const incomeOtherPostTax = totalForPeriod(countedIncomes, row => row.amountPostTax, otherPeriod);
  const expenditureOther = totalForPeriod(countedExpenditures, row => row.amount, otherPeriod);

  const incomeLeftOut = rowLeftOutNames(countedIncomes, incomeLeftOutReason, incomeName, {
    nameMembers,
  });
  const expenditureLeftOut = rowLeftOutNames(
    countedExpenditures,
    expenditureLeftOutReason,
    expenditureName,
    { nameMembers },
  );
  const lifeEventLeftOut = rowLeftOutNames(
    countedLifeEvents,
    lifeEventLeftOutReason,
    lifeEventName,
    { nameMembers },
  );
  // Net position sets spending against after-tax income, so only a missing after-tax amount
  // leaves an income out of it.
  const netPositionLeftOut = combineLeftOutNames(
    rowLeftOutNames(countedIncomes, afterTaxLeftOutReason, incomeName, {
      section: 'Income',
      nameMembers,
    }),
    rowLeftOutNames(countedExpenditures, expenditureLeftOutReason, expenditureName, {
      section: 'Expenditure',
      nameMembers,
    }),
  );

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <PeriodToggle testId="cashflows-period" period={period} onPeriodChange={onPeriodChange} />
      </Group>

      {banner}

      <CashflowSection
        value="incomes"
        title="Income"
        count={incomeRows.length}
        showCount={false}
        onAdd={incomeActions?.onAdd}
        totalNode={
          incomeRows.length === 0 ? (
            <SectionTotalWithLabel>{NO_FIGURE}</SectionTotalWithLabel>
          ) : (
            <SectionTotalWithLabel
              tooltipLabel={`${otherFrequency}: +${formatMoney(incomeOtherPreTax, currency)} (${formatMoney(incomeOtherPostTax, currency)} after tax)`}
              leftOut={incomeLeftOut}
              leftOutLabels={INCOME_TOTAL_REASON_LABELS}
            >
              +{formatMoney(totals.incomePreTax, currency)} (
              {formatMoney(totals.incomePostTax, currency)} after tax)
            </SectionTotalWithLabel>
          )
        }
      >
        <CashflowTable<IncomeRow>
          items={incomeRows}
          columns={incomeRowColumns(period, currency, nameMembers)}
          onRowClick={incomeActions?.onRowClick}
        />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditureRows.length}
        showCount={false}
        onAdd={expenditureActions?.onAdd}
        totalNode={
          expenditureRows.length === 0 ? (
            <SectionTotalWithLabel>{NO_FIGURE}</SectionTotalWithLabel>
          ) : (
            <SectionTotalWithLabel
              tooltipLabel={`${otherFrequency}: -${formatMoney(expenditureOther, currency)}`}
              leftOut={expenditureLeftOut}
            >
              -{formatMoney(totals.expenditure, currency)}
            </SectionTotalWithLabel>
          )
        }
      >
        <CashflowTable<ExpenditureRow>
          items={expenditureRows}
          columns={expenditureRowColumns(period, currency, nameMembers)}
          onRowClick={expenditureActions?.onRowClick}
        />
      </CashflowSection>

      {hasNetPosition && <NetPosition amount={netPosition} leftOut={netPositionLeftOut} />}

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEventRows.length}
        showCount={false}
        onAdd={lifeEventActions?.onAdd}
        totalNode={
          lifeEventRows.length === 0 ? (
            <SectionTotalWithLabel>{NO_FIGURE}</SectionTotalWithLabel>
          ) : (
            <SectionTotalWithLabel leftOut={lifeEventLeftOut}>
              -{formatMoney(totals.lifeEvents, currency)}
            </SectionTotalWithLabel>
          )
        }
      >
        <CashflowTable<LifeEventRow>
          items={lifeEventRows}
          columns={lifeEventRowColumns(currency, nameMembers)}
          onRowClick={lifeEventActions?.onRowClick}
        />
      </CashflowSection>

      {modelsSection}
    </Stack>
  );
}

function SectionTotalWithLabel({
  children,
  tooltipLabel,
  leftOut,
  leftOutLabels,
}: {
  children: ReactNode;
  tooltipLabel?: string;
  leftOut?: LeftOutNames;
  leftOutLabels?: ReasonLabels;
}) {
  // The total sits over the section's accordion control, so only the figure and the left-out
  // note may take pointer events - their tooltips need them. A label taking them too swallowed
  // the clicks that expand the section.
  return (
    <Stack gap={2} align="flex-end" style={{ pointerEvents: 'none' }}>
      <Flex align="baseline" gap={6} wrap="nowrap">
        <Text size="lg" c="gray.4" style={{ whiteSpace: 'nowrap' }}>
          Total:
        </Text>
        <Box style={{ pointerEvents: 'auto' }}>
          <SectionTotal tooltipLabel={tooltipLabel}>{children}</SectionTotal>
        </Box>
      </Flex>
      {leftOut && (
        <Box style={{ pointerEvents: 'auto' }}>
          <LeftOutNote names={leftOut} labels={leftOutLabels} />
        </Box>
      )}
    </Stack>
  );
}

function NetPosition({ amount, leftOut }: { amount: number; leftOut: LeftOutNames }) {
  const currency = useCurrency();
  const isPositive = amount >= 0;

  return (
    <Stack gap={2} align="flex-end" pr={48} data-testid="cashflows-net-position">
      <Group gap={8}>
        <Text size="lg" c="gray.4">
          Net position:
        </Text>
        <Text size="lg" fw={FONT_WEIGHTS.bold} c={isPositive ? 'green.7' : 'red.7'}>
          {isPositive ? '+' : '-'}
          {formatMoney(Math.abs(amount), currency)}
        </Text>
      </Group>
      <LeftOutNote names={leftOut} />
    </Stack>
  );
}
